import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { supabase } from "./supabase.js";
import { pipeTtsErrorToServer } from "./ttsClientLog.js";
import {
  configurePlaybackAudio,
  describePlaybackError,
  primeMediaPlayback,
} from "./mediaPlayback.js";
import {
  TtsRenderCore,
  buildTtsChunks,
  getTimestampDuration,
  type LineTimestamp,
  type TtsChunk,
  type TtsPreparedChunk,
} from "./ttsCore.js";
import type {
  StoreAction,
  TtsPlayFromTarget,
  UserPreferences,
} from "../store/types.js";
import type { TtsSettings } from "../../shared/tts.js";
import {
  getCurrentTtsVoice,
  getTtsProvider,
  getTtsSettingsSignature,
  resolveTtsSettings,
} from "../../shared/tts.js";
import {
  createInitialPlaybackControllerState,
  playbackControllerReducer,
  type OutputOwner,
  type PlaybackControllerState,
} from "./ttsPlaybackMachine.js";

const PENDING_ACTIVITY = "Preparing first segment...";
const NEXT_ACTIVITY = "Preparing next segment...";
const PRECISE_SEEK_STT_WAIT_MS = 1400;
const FULL_PREP_CONCURRENCY = 6;
const PLAY_FROM_WORD_BACKOFF = 1;
const AVG_CHARS_PER_WORD = 6;
const STALE_PREP_ERROR = "tts-preparation-superseded";
const WARMUP_BUDGET_SEC = 120;

export type ChunkAudioStatus =
  | "pending"
  | "preparing"
  | "ready-cached"
  | "playing"
  | "error";

export type ChunkTimingStatus = "pending" | "ready" | "missing";

export interface ChunkRuntimeStatus {
  audio: ChunkAudioStatus;
  timing: ChunkTimingStatus;
  note?: string;
}

interface UseTtsPlaybackControllerArgs {
  openFilePath: string | null;
  content: string | null;
  projectId: string;
  userPrefs: UserPreferences;
  ttsFromLine: TtsPlayFromTarget | null;
  storeDispatch: (action: StoreAction) => void;
  onTtsSettingsChange: (settings: TtsSettings) => void;
}

interface PlaybackCommandToken {
  sessionId: number;
  commandId: number;
}

interface PlayChunkOptions extends PlaybackCommandToken {
  startAt?: number;
  force?: boolean;
  seekLine?: number;
  seekChar?: number;
  preferPreciseSeek?: boolean;
  autoplay?: boolean;
}

export interface TtsPlaybackController {
  state: PlaybackControllerState;
  ttsSettings: TtsSettings;
  setTtsSettings: (
    next: TtsSettings | ((current: TtsSettings) => TtsSettings)
  ) => void;
  clearError: () => void;
  setVolume: (next: number | ((current: number) => number)) => void;
  play: () => void;
  playFrom: (fromTarget?: number | { fromLine: number; fromChar?: number }) => Promise<void>;
  pause: () => void;
  stop: () => void;
  stopPreview: () => void;
  seekToGlobalTime: (targetSeconds: number) => void;
  skipBack15: () => void;
  skipNextChunk: () => void;
  previewVoice: (request: {
    providerId: TtsSettings["provider"];
    voiceId: string;
    modelId: string;
    event: ReactMouseEvent;
  }) => Promise<void>;
  downloadAudio: () => Promise<void>;
  regenerate: () => void;
  effectiveDuration: number;
  hasContent: boolean;
  isMainActive: boolean;
  currentVoiceName: string;
  providerName: string;
  chunkPlan: TtsChunk[];
  chunkStatuses: Map<number, ChunkRuntimeStatus>;
  chunkDurations: number[];
  currentChunkIndex: number | null;
  currentChunkNumber: number;
  preparedChunkCount: number;
  timestampReadyCount: number;
  downloadBusy: boolean;
  followAlong: boolean;
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function getChunkStartLines(content: string): number[] {
  const lines = content.split("\n");
  const starts: number[] = [1];
  for (let i = 1; i < lines.length; i++) {
    const prev = lines[i - 1];
    const cur = lines[i];
    if ((prev.trim() === "" && cur.trim() !== "") || /^#{1,6}\s/.test(cur)) {
      const lineNum = i + 1;
      if (starts[starts.length - 1] !== lineNum) starts.push(lineNum);
    }
  }
  return starts;
}

function estimateChunkDurationSeconds(chunk: TtsChunk): number {
  return Math.max(2.5, chunk.text.length / 16);
}

function sum(values: number[]): number {
  let total = 0;
  for (const value of values) {
    if (Number.isFinite(value) && value > 0) total += value;
  }
  return total;
}

function getPreciseSeekStart(
  timestamps: LineTimestamp[],
  seekLine?: number,
  seekChunkChar?: number
): number | null {
  if (timestamps.length === 0) return null;

  if (typeof seekChunkChar === "number" && Number.isFinite(seekChunkChar)) {
    const backoffChars = PLAY_FROM_WORD_BACKOFF * AVG_CHARS_PER_WORD;
    const targetChar = Math.max(0, seekChunkChar - backoffChars);
    const containing = timestamps.find(
      (entry) => targetChar >= entry.fromChar && targetChar <= entry.toChar
    );
    if (containing) {
      const span = Math.max(1, containing.toChar - containing.fromChar);
      const ratio = Math.max(
        0,
        Math.min(1, (targetChar - containing.fromChar) / span)
      );
      return containing.start + ratio * (containing.end - containing.start);
    }

    const forward = timestamps.find((entry) => entry.fromChar >= targetChar);
    if (forward) return forward.start;

    let backward: LineTimestamp | null = null;
    for (const entry of timestamps) {
      if (entry.toChar <= targetChar) backward = entry;
    }
    if (backward) return backward.start;
  }

  if (seekLine && seekLine > 1) {
    const lineTarget = timestamps.find((entry) => entry.line >= seekLine);
    if (lineTarget) return lineTarget.start;
  }

  return timestamps[0]?.start ?? null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function abortAudioElement(audio: HTMLAudioElement | null) {
  if (!audio) return;
  try {
    audio.pause();
  } catch {
    // Ignore teardown pause failures.
  }
  try {
    audio.currentTime = 0;
  } catch {
    // Ignore teardown seek failures.
  }
  try {
    audio.removeAttribute("src");
    audio.load();
  } catch {
    // Ignore teardown source reset failures.
  }
}

function makePreviewKey(providerId: TtsSettings["provider"], voiceId: string): string {
  return `${providerId}:${voiceId}`;
}

function isStalePreparationError(error: unknown): boolean {
  return error instanceof Error && error.message === STALE_PREP_ERROR;
}

export function useTtsPlaybackController({
  openFilePath,
  content,
  projectId,
  userPrefs,
  ttsFromLine,
  storeDispatch,
  onTtsSettingsChange,
}: UseTtsPlaybackControllerArgs): TtsPlaybackController {
  const [state, dispatchState] = useReducer(
    playbackControllerReducer,
    undefined,
    createInitialPlaybackControllerState
  );
  const stateRef = useRef(state);
  stateRef.current = state;

  const ttsSettingsRef = useRef(state.ttsSettings);
  const volumeRef = useRef(state.volume);
  const followAlongRef = useRef(state.ttsSettings.followAlong);
  const simpleModeRef = useRef(state.ttsSettings.simpleMode);

  useEffect(() => {
    ttsSettingsRef.current = state.ttsSettings;
    volumeRef.current = state.volume;
    followAlongRef.current = state.ttsSettings.followAlong;
    simpleModeRef.current = state.ttsSettings.simpleMode;
  }, [state.ttsSettings, state.volume]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const ownerRef = useRef<OutputOwner>("none");
  const sessionIdRef = useRef(0);
  const commandIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const coreRef = useRef<TtsRenderCore | null>(null);
  const coreProjectIdRef = useRef<string | null>(null);
  const chunkPlanRef = useRef<TtsChunk[]>([]);
  const preparedChunksRef = useRef<Map<number, TtsPreparedChunk>>(new Map());
  const chunkAudioPrepRef = useRef<Map<number, Promise<TtsPreparedChunk>>>(new Map());
  const chunkDurationsRef = useRef<number[]>([]);
  const currentChunkRef = useRef<TtsChunk | null>(null);
  const currentChunkIndexRef = useRef(0);
  const forceRunRef = useRef(false);
  const forceRegenRef = useRef(false);
  const fullPrepPromiseRef = useRef<Promise<void> | null>(null);
  const fullPrepKeyRef = useRef("");
  const fullPrepRunIdRef = useRef(0);
  const warmupCeilingRef = useRef(0);
  const prepGenerationRef = useRef(0);
  const blobUrlsRef = useRef<Set<string>>(new Set());
  const timestampsRef = useRef<LineTimestamp[]>([]);
  const missingTimingLoggedRef = useRef(false);
  const settingsHydratedRef = useRef(false);
  const skipNextSettingsSaveRef = useRef(false);
  const onTtsSettingsChangeRef = useRef(onTtsSettingsChange);
  const chunkStatusRef = useRef<Map<number, ChunkRuntimeStatus>>(new Map());
  const lastHighlightLineRef = useRef<number | null>(null);
  const applyChunkTimestampsRef = useRef<(chunkIndex: number, ts: LineTimestamp[]) => void>(
    () => {}
  );
  const playChunkRef = useRef<((chunkIndex: number, options: PlayChunkOptions) => Promise<void>) | null>(
    null
  );
  const warmAllChunksRef = useRef<((chunks: TtsChunk[], force?: boolean, startFrom?: number) => Promise<void>) | null>(
    null
  );
  const [downloadBusy, setDownloadBusy] = useReducer((_: boolean, next: boolean) => next, false);

  const dispatchUi = useCallback((action: Parameters<typeof dispatchState>[0]) => {
    if (action.type === "details:bump") {
      startTransition(() => dispatchState(action));
      return;
    }
    dispatchState(action);
  }, []);

  const bumpDetails = useCallback(() => {
    dispatchUi({ type: "details:bump" });
  }, [dispatchUi]);

  const accountTts = useMemo(
    () => resolveTtsSettings(userPrefs.tts),
    [userPrefs.tts]
  );

  useEffect(() => {
    onTtsSettingsChangeRef.current = onTtsSettingsChange;
  }, [onTtsSettingsChange]);

  useEffect(() => {
    dispatchUi({ type: "settings:hydrate", settings: accountTts });
    settingsHydratedRef.current = true;
    skipNextSettingsSaveRef.current = true;
  }, [
    dispatchUi,
    accountTts.provider,
    accountTts.simpleMode,
    accountTts.followAlong,
    accountTts.elevenlabs.voiceId,
    accountTts.elevenlabs.modelId,
    accountTts.elevenlabs.stability,
    accountTts.elevenlabs.similarityBoost,
    accountTts.elevenlabs.style,
    accountTts.elevenlabs.speed,
    accountTts.elevenlabs.enhanceWithAudioTags,
    accountTts.openai.voiceId,
    accountTts.openai.modelId,
    accountTts.openai.speed,
    accountTts.openai.instructions,
  ]);

  const setTtsSettings = useCallback(
    (next: TtsSettings | ((current: TtsSettings) => TtsSettings)) => {
      const current = stateRef.current.ttsSettings;
      const resolved = resolveTtsSettings(
        typeof next === "function" ? next(current) : next
      );
      dispatchUi({ type: "settings:set", settings: resolved });
    },
    [dispatchUi]
  );

  useEffect(() => {
    if (!settingsHydratedRef.current) return;
    if (skipNextSettingsSaveRef.current) {
      skipNextSettingsSaveRef.current = false;
      return;
    }

    onTtsSettingsChangeRef.current(stateRef.current.ttsSettings);
  }, [state.ttsSettings]);

  const setVolume = useCallback(
    (next: number | ((current: number) => number)) => {
      const current = stateRef.current.volume;
      const value =
        typeof next === "function" ? next(current) : next;
      dispatchUi({
        type: "volume:set",
        volume: Math.max(0, Math.min(1, value)),
      });
    },
    [dispatchUi]
  );

  const clearError = useCallback(() => {
    dispatchUi({ type: "error:clear" });
  }, [dispatchUi]);

  const setActivity = useCallback(
    (activity: string | null) => {
      dispatchUi({ type: "activity:set", activity });
    },
    [dispatchUi]
  );

  const resetTransport = useCallback(
    (error?: string | null) => {
      ownerRef.current = "none";
      dispatchUi({ type: "transport:reset", error });
    },
    [dispatchUi]
  );

  const reportTtsClientLog = useCallback(
    (
      scope: string,
      message: string,
      options?: {
        level?: "debug" | "info" | "warn" | "error";
        error?: unknown;
        meta?: Record<string, unknown>;
      }
    ) => {
      void pipeTtsErrorToServer({
        scope,
        message,
        level: options?.level || "info",
        error: options?.error,
        meta: options?.meta,
        projectId,
      });
    },
    [projectId]
  );

  const reportTtsClientError = useCallback(
    (
      scope: string,
      message: string,
      error?: unknown,
      meta?: Record<string, unknown>
    ) => {
      reportTtsClientLog(scope, message, {
        level: "error",
        error,
        meta,
      });
    },
    [reportTtsClientLog]
  );

  const nextCommand = useCallback(
    ({
      activeOutput,
      status,
      newSession = false,
      activity,
      clearError = true,
      previewingVoice,
      resetTimeline = false,
    }: {
      activeOutput: OutputOwner;
      status: PlaybackControllerState["status"];
      newSession?: boolean;
      activity?: string | null;
      clearError?: boolean;
      previewingVoice?: string | null;
      resetTimeline?: boolean;
    }): PlaybackCommandToken => {
      commandIdRef.current += 1;
      if (newSession) {
        sessionIdRef.current += 1;
      }
      ownerRef.current = activeOutput;
      const token = {
        sessionId: sessionIdRef.current,
        commandId: commandIdRef.current,
      };
      dispatchUi({
        type: "command:start",
        status,
        activeOutput,
        sessionId: token.sessionId,
        commandId: token.commandId,
        activity,
        clearError,
        previewingVoice,
        resetTimeline,
      });
      return token;
    },
    [dispatchUi]
  );

  const isCurrentCommand = useCallback((commandId: number) => {
    return isMountedRef.current && commandIdRef.current === commandId;
  }, []);

  const isCurrentMainCommand = useCallback(
    (sessionId: number, commandId: number) =>
      isMountedRef.current &&
      ownerRef.current === "main" &&
      sessionIdRef.current === sessionId &&
      commandIdRef.current === commandId,
    []
  );

  const clearMainAudio = useCallback(() => {
    const audio = audioRef.current;
    audioRef.current = null;
    abortAudioElement(audio);
  }, []);

  const clearPreviewAudio = useCallback(() => {
    const audio = previewAudioRef.current;
    previewAudioRef.current = null;
    abortAudioElement(audio);
  }, []);

  const clearBlobUrls = useCallback(() => {
    for (const url of blobUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    blobUrlsRef.current.clear();
  }, []);

  const setChunkStatus = useCallback(
    (chunkIndex: number, patch: Partial<ChunkRuntimeStatus>) => {
      const current = chunkStatusRef.current.get(chunkIndex) || {
        audio: "pending",
        timing: "pending",
      };
      chunkStatusRef.current.set(chunkIndex, { ...current, ...patch });
      bumpDetails();
    },
    [bumpDetails]
  );

  const getAccessToken = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? "";
  }, []);

  const ensureCore = useCallback(() => {
    const settings = resolveTtsSettings(ttsSettingsRef.current);
    if (!coreRef.current || coreProjectIdRef.current !== projectId) {
      coreRef.current = new TtsRenderCore({
        projectId,
        getAccessToken,
        settings,
      });
      coreProjectIdRef.current = projectId;
      return coreRef.current;
    }
    coreRef.current.updateSettings(settings);
    return coreRef.current;
  }, [getAccessToken, projectId]);

  const recalcTimelineDuration = useCallback(() => {
    const total = sum(chunkDurationsRef.current);
    dispatchUi({
      type: "transport:update",
      mediaDuration: total,
      timingDuration: total,
    });
    return total;
  }, [dispatchUi]);

  const chunkStartTime = useCallback((chunkIndex: number) => {
    let acc = 0;
    for (let i = 0; i < chunkIndex; i++) {
      acc += chunkDurationsRef.current[i] || 0;
    }
    return acc;
  }, []);

  const dispatchPlaybackFromCurrentTime = useCallback(
    (audio: HTMLAudioElement) => {
      const chunk = currentChunkRef.current;
      const ts = timestampsRef.current;
      if (!chunk) return;
      if (!followAlongRef.current) return;

      if (ts.length === 0) {
        if (!missingTimingLoggedRef.current) {
          const message =
            "Missing timing information; cursor animation disabled until real timestamps are available";
          reportTtsClientError(
            "audio_bar.missing_timestamps",
            message,
            undefined,
            {
              chunkIndex: chunk.index,
              lineOffset: chunk.lineOffset,
              lineCount: chunk.lineCount,
            }
          );
          missingTimingLoggedRef.current = true;
          storeDispatch({ type: "tts:highlight-clear" });
          storeDispatch({ type: "tts:playback-stop" });
        }
        return;
      }

      missingTimingLoggedRef.current = false;

      const t = audio.currentTime;
      let best: LineTimestamp | null = null;
      for (const s of ts) {
        if (t >= s.start) best = s;
        else break;
      }
      if (!best) {
        storeDispatch({ type: "tts:playback-stop" });
        return;
      }

      const segmentDuration = Math.max(0.001, best.end - best.start);
      const segmentElapsed = Math.max(0, Math.min(segmentDuration, t - best.start));
      const fromChar = chunk.charOffset + best.fromChar;
      const toChar = chunk.charOffset + best.toChar;
      lastHighlightLineRef.current = best.line;

      storeDispatch({
        type: "tts:highlight",
        line: best.line,
        fromChar,
        toChar,
      });
      if (simpleModeRef.current) {
        return;
      }
      storeDispatch({
        type: "tts:playback",
        playback: {
          fromChar,
          toChar,
          duration: segmentDuration,
          elapsed: segmentElapsed,
          dispatchedAt: Date.now(),
          playing: !audio.paused,
        },
      });
    },
    [reportTtsClientError, storeDispatch]
  );

  const updatePlaybackFromAudio = useCallback(
    (audio: HTMLAudioElement) => {
      const total = sum(chunkDurationsRef.current);
      const start = chunkStartTime(currentChunkIndexRef.current);
      const now = start + audio.currentTime;
      dispatchUi({
        type: "transport:update",
        currentTime: now,
        progress: total > 0 ? Math.max(0, Math.min(1, now / total)) : 0,
      });
      dispatchPlaybackFromCurrentTime(audio);
    },
    [chunkStartTime, dispatchPlaybackFromCurrentTime, dispatchUi]
  );

  const applyChunkTimestamps = useCallback(
    (chunkIndex: number, ts: LineTimestamp[]) => {
      if (ts.length === 0) {
        setChunkStatus(chunkIndex, { timing: "missing" });
        return;
      }
      setChunkStatus(chunkIndex, { timing: "ready" });
      const duration = getTimestampDuration(ts);
      if (duration > 0) {
        chunkDurationsRef.current[chunkIndex] = duration;
        recalcTimelineDuration();
      }
      if (chunkIndex === currentChunkIndexRef.current) {
        timestampsRef.current = ts;
        missingTimingLoggedRef.current = false;
        const audio = audioRef.current;
        if (audio) updatePlaybackFromAudio(audio);
      }
    },
    [recalcTimelineDuration, setChunkStatus, updatePlaybackFromAudio]
  );
  applyChunkTimestampsRef.current = applyChunkTimestamps;

  const ensurePreparedChunk = useCallback(
    async (
      chunkIndex: number,
      options: {
        force?: boolean;
        waitForTimestamps?: boolean;
      } = {}
    ): Promise<TtsPreparedChunk> => {
      const { force = false, waitForTimestamps = false } = options;
      const chunk = chunkPlanRef.current[chunkIndex];
      const prepGeneration = prepGenerationRef.current;
      if (!chunk) {
        throw new Error(`Chunk ${chunkIndex + 1} not found`);
      }

      const assertPreparationCurrent = () => {
        if (prepGenerationRef.current !== prepGeneration) {
          throw new Error(STALE_PREP_ERROR);
        }
      };

      if (force) {
        chunkAudioPrepRef.current.delete(chunkIndex);
      } else {
        const existing = chunkAudioPrepRef.current.get(chunkIndex);
        if (existing) {
          const prepared = await existing;
          assertPreparationCurrent();
          if (!waitForTimestamps) return prepared;
          const ts =
            prepared.timestamps.length > 0
              ? prepared.timestamps
              : await prepared.timestampsReady;
          assertPreparationCurrent();
          prepared.timestamps = ts;
          applyChunkTimestampsRef.current(chunkIndex, ts);
          setChunkStatus(chunkIndex, {
            timing: ts.length > 0 ? "ready" : "missing",
            note: ts.length > 0 ? `timestamps ${ts.length}` : "timestamps unavailable",
          });
          return prepared;
        }
      }

      const run = (async () => {
        const core = ensureCore();
        if (force) {
          core.invalidateChunk(chunk);
          preparedChunksRef.current.delete(chunkIndex);
        }

        setChunkStatus(chunkIndex, {
          audio: "preparing",
          timing: "pending",
          note: "requesting audio",
        });

        let prepared = preparedChunksRef.current.get(chunkIndex);
        if (!prepared) {
          prepared = await core.prepareChunk(chunk, { force });
        }
        assertPreparationCurrent();

        preparedChunksRef.current.set(chunkIndex, prepared);

        setChunkStatus(chunkIndex, {
          audio: "ready-cached",
          timing: prepared.timestamps.length > 0 ? "ready" : "pending",
          note: prepared.timestamps.length > 0 ? "ready" : "waiting timestamps",
        });

        if (prepared.timestamps.length > 0) {
          applyChunkTimestampsRef.current(chunkIndex, prepared.timestamps);
        }

        if (waitForTimestamps) {
          const ts =
            prepared.timestamps.length > 0
              ? prepared.timestamps
              : await prepared.timestampsReady;
          assertPreparationCurrent();
          prepared.timestamps = ts;
          applyChunkTimestampsRef.current(chunkIndex, ts);
          setChunkStatus(chunkIndex, {
            timing: ts.length > 0 ? "ready" : "missing",
            note: ts.length > 0 ? `timestamps ${ts.length}` : "timestamps unavailable",
          });
        }

        return prepared;
      })().catch((err) => {
        chunkAudioPrepRef.current.delete(chunkIndex);
        if (isStalePreparationError(err)) {
          return Promise.reject(err);
        }
        setChunkStatus(chunkIndex, {
          audio: "error",
          timing: "missing",
          note: err instanceof Error ? err.message : "prepare failed",
        });
        throw err;
      });

      chunkAudioPrepRef.current.set(chunkIndex, run);
      return run;
    },
    [applyChunkTimestamps, ensureCore, setChunkStatus]
  );

  const warmAllChunks = useCallback(
    (chunks: TtsChunk[], force = false, startFrom = 0): Promise<void> => {
      if (chunks.length === 0) return Promise.resolve();
      const settingsKey = getTtsSettingsSignature(ttsSettingsRef.current);
      const key = `${settingsKey}:${chunks.map((chunk) => chunk.key).join(",")}:${force ? "force" : "normal"}:${startFrom}`;
      if (!force && fullPrepPromiseRef.current && fullPrepKeyRef.current === key) {
        return fullPrepPromiseRef.current;
      }

      const runId = fullPrepRunIdRef.current + 1;
      fullPrepRunIdRef.current = runId;
      fullPrepKeyRef.current = key;

      // Only prepare forward from startFrom, capped at ~2 minutes of estimated audio
      const safeStart = Math.max(0, Math.min(startFrom, chunks.length - 1));
      const ahead: number[] = [];
      let budgetSec = WARMUP_BUDGET_SEC;
      for (let i = safeStart; i < chunks.length && budgetSec > 0; i++) {
        ahead.push(i);
        budgetSec -= estimateChunkDurationSeconds(chunks[i]);
      }
      const ceiling = ahead.length > 0 ? ahead[ahead.length - 1] : safeStart;
      warmupCeilingRef.current = ceiling;

      const run = (async () => {
        let done = 0;
        let cursor = 0;
        const total = ahead.length;
        reportTtsClientLog("audio_bar.full_prep.start", "Started ahead-of-playback audio prep", {
          level: "info",
          meta: {
            chunkCount: total,
            totalChunks: chunks.length,
            force,
            startFrom: safeStart,
          },
        });
        setActivity(`Preparing audio (0/${total})...`);

        const worker = async () => {
          while (true) {
            const idx = cursor++;
            if (idx >= total) return;
            const chunkIndex = chunks[ahead[idx]].index;
            const prepared = await ensurePreparedChunk(chunkIndex, {
              force,
              waitForTimestamps: false,
            });
            done += 1;
            reportTtsClientLog("audio_bar.full_prep.chunk", "Prepared chunk audio", {
              level: "info",
              meta: {
                chunkIndex,
                done,
                total,
                timestampCount: prepared.timestamps.length,
              },
            });
            if (fullPrepRunIdRef.current === runId) {
              setActivity(`Preparing audio (${done}/${total})...`);
            }
          }
        };

        const workers = Math.max(1, Math.min(FULL_PREP_CONCURRENCY, total));
        await Promise.all(Array.from({ length: workers }, () => worker()));

        // Second pass: collect timestamps for prepared chunks
        if (fullPrepRunIdRef.current === runId) {
          for (const idx of ahead) {
            if (fullPrepRunIdRef.current !== runId) break;
            const chunkIndex = chunks[idx].index;
            try {
              await ensurePreparedChunk(chunkIndex, {
                force: false,
                waitForTimestamps: true,
              });
            } catch {
              // Non-fatal: timestamps are optional for playback
            }
          }
        }

        if (fullPrepRunIdRef.current === runId) {
          reportTtsClientLog("audio_bar.full_prep.done", "Completed ahead-of-playback audio prep", {
            level: "info",
            meta: {
              chunkCount: total,
            },
          });
          setActivity(null);
        }
      })().catch((err) => {
        if (isStalePreparationError(err)) {
          return;
        }
        if (fullPrepRunIdRef.current === runId) {
          reportTtsClientError(
            "audio_bar.full_prep",
            "Ahead-of-playback preparation failed",
            err,
            { chunkCount: ahead.length }
          );
          setActivity(null);
        }
        throw err;
      });

      fullPrepPromiseRef.current = run;
      return run;
    },
    [ensurePreparedChunk, reportTtsClientError, reportTtsClientLog, setActivity]
  );
  warmAllChunksRef.current = warmAllChunks;

  const teardownAll = useCallback(() => {
    prepGenerationRef.current += 1;
    fullPrepRunIdRef.current += 1;
    clearMainAudio();
    clearPreviewAudio();
    chunkAudioPrepRef.current.clear();
    preparedChunksRef.current.clear();
    chunkPlanRef.current = [];
    currentChunkRef.current = null;
    currentChunkIndexRef.current = 0;
    timestampsRef.current = [];
    chunkDurationsRef.current = [];
    chunkStatusRef.current.clear();
    lastHighlightLineRef.current = null;
    fullPrepPromiseRef.current = null;
    fullPrepKeyRef.current = "";
    warmupCeilingRef.current = 0;
    coreRef.current?.clear();
    clearBlobUrls();
    bumpDetails();
  }, [bumpDetails, clearBlobUrls, clearMainAudio, clearPreviewAudio]);

  const stopPreview = useCallback(() => {
    if (
      ownerRef.current !== "preview" &&
      !previewAudioRef.current &&
      stateRef.current.previewingVoice === null
    ) {
      return;
    }
    nextCommand({
      activeOutput: "none",
      status: "idle",
      activity: null,
      previewingVoice: null,
    });
    clearPreviewAudio();
    dispatchUi({ type: "preview:set", previewingVoice: null });
  }, [clearPreviewAudio, dispatchUi, nextCommand]);

  const stop = useCallback(() => {
    nextCommand({
      activeOutput: "none",
      status: "stopping",
      newSession: true,
      activity: null,
      previewingVoice: null,
      resetTimeline: true,
    });
    teardownAll();
    forceRunRef.current = false;
    missingTimingLoggedRef.current = false;
    storeDispatch({ type: "tts:clear" });
    resetTransport();
  }, [nextCommand, resetTransport, storeDispatch, teardownAll]);

  const playChunk = useCallback(
    async (chunkIndex: number, options: PlayChunkOptions) => {
      const {
        sessionId,
        commandId,
        startAt = 0,
        force = false,
        seekLine,
        seekChar,
        preferPreciseSeek = false,
        autoplay = true,
      } = options;
      const commandIsCurrent = () => isCurrentMainCommand(sessionId, commandId);
      const chunk = chunkPlanRef.current[chunkIndex];
      if (!chunk) {
        if (!commandIsCurrent()) return;
        currentChunkRef.current = null;
        currentChunkIndexRef.current = 0;
        timestampsRef.current = [];
        lastHighlightLineRef.current = null;
        storeDispatch({ type: "tts:highlight-clear" });
        storeDispatch({ type: "tts:playback-stop" });
        storeDispatch({ type: "tts:clear" });
        resetTransport();
        return;
      }

      if (!commandIsCurrent()) return;

      clearMainAudio();
      currentChunkRef.current = chunk;
      currentChunkIndexRef.current = chunkIndex;
      timestampsRef.current = [];
      missingTimingLoggedRef.current = false;

      for (const [idx, status] of chunkStatusRef.current.entries()) {
        if (status.audio === "playing") {
          chunkStatusRef.current.set(idx, {
            ...status,
            audio: "ready-cached",
          });
        }
      }
      bumpDetails();
      setChunkStatus(chunkIndex, {
        audio: "preparing",
        timing: "pending",
        note: "requesting audio",
      });
      setActivity(chunkIndex === 0 ? PENDING_ACTIVITY : NEXT_ACTIVITY);

      let prepared: TtsPreparedChunk;
      try {
        prepared = await ensurePreparedChunk(chunkIndex, {
          force,
          waitForTimestamps: false,
        });
      } catch (err: any) {
        if (!commandIsCurrent()) return;
        if (isStalePreparationError(err)) return;
        reportTtsClientError(
          "audio_bar.prepare_chunk",
          "Chunk preparation failed",
          err,
          {
            chunkIndex,
            lineOffset: chunk.lineOffset,
            lineCount: chunk.lineCount,
            force,
          }
        );
        setChunkStatus(chunkIndex, {
          audio: "error",
          timing: "missing",
          note: err?.message || "failed to prepare",
        });
        throw err;
      }

      if (!commandIsCurrent()) return;

      setChunkStatus(chunkIndex, {
        audio: "ready-cached",
        timing: prepared.timestamps.length > 0 ? "ready" : "pending",
        note: prepared.timestamps.length > 0 ? "ready" : "waiting timestamps",
      });
      setActivity(null);

      if (prepared.timestamps.length > 0) {
        timestampsRef.current = prepared.timestamps;
        applyChunkTimestamps(chunkIndex, prepared.timestamps);
      }

      const seekChunkChar =
        typeof seekChar === "number" && Number.isFinite(seekChar)
          ? Math.max(0, seekChar - chunk.charOffset)
          : undefined;

      if (
        preferPreciseSeek &&
        prepared.timestamps.length === 0 &&
        (seekLine || (typeof seekChunkChar === "number" && Number.isFinite(seekChunkChar)))
      ) {
        setActivity("Aligning exact word...");
        const maybeTimestamps = await Promise.race([
          prepared.timestampsReady.then((ts) => ts),
          new Promise<null>((resolve) =>
            setTimeout(() => resolve(null), PRECISE_SEEK_STT_WAIT_MS)
          ),
        ]);
        if (!commandIsCurrent()) return;
        if (maybeTimestamps && maybeTimestamps.length > 0) {
          prepared.timestamps = maybeTimestamps;
          applyChunkTimestamps(chunkIndex, maybeTimestamps);
          setChunkStatus(chunkIndex, {
            timing: "ready",
            note: `timestamps ${maybeTimestamps.length}`,
          });
        }
      }

      let preciseSeekApplied = false;
      void prepared.timestampsReady.then((ts) => {
        if (!commandIsCurrent()) return;
        prepared.timestamps = ts;
        applyChunkTimestamps(chunkIndex, ts);
        if (ts.length === 0) {
          setChunkStatus(chunkIndex, { timing: "missing" });
        } else {
          setChunkStatus(chunkIndex, {
            timing: "ready",
            note: `timestamps ${ts.length}`,
          });
          if (!preciseSeekApplied && chunkIndex === currentChunkIndexRef.current) {
            const preciseStart = getPreciseSeekStart(ts, seekLine, seekChunkChar);
            const activeAudio = audioRef.current;
            if (
              typeof preciseStart === "number" &&
              Number.isFinite(preciseStart) &&
              activeAudio
            ) {
              const drift = preciseStart - activeAudio.currentTime;
              if (drift > 0.05 || activeAudio.currentTime < 0.25) {
                activeAudio.currentTime = preciseStart;
                updatePlaybackFromAudio(activeAudio);
              }
              preciseSeekApplied = true;
            }
          }
        }
        if (chunkIndex === currentChunkIndexRef.current && ts.length > 0) {
          setActivity(null);
        }
      });

      const audio = configurePlaybackAudio(new Audio(), volumeRef.current);
      if (!commandIsCurrent()) {
        abortAudioElement(audio);
        return;
      }
      audioRef.current = audio;

      let seekApplied = false;
      let desiredStart = Math.max(0, startAt);
      const initialPreciseStart = getPreciseSeekStart(
        prepared.timestamps,
        seekLine,
        seekChunkChar
      );
      if (typeof initialPreciseStart === "number" && Number.isFinite(initialPreciseStart)) {
        desiredStart = initialPreciseStart;
        preciseSeekApplied = true;
      }
      const applySeek = () => {
        if (seekApplied || desiredStart <= 0) return;
        if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
        audio.currentTime = Math.min(desiredStart, Math.max(0, audio.duration - 0.05));
        seekApplied = true;
      };

      const ownsAudio = () => commandIsCurrent() && audioRef.current === audio;

      const updateChunkDuration = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          chunkDurationsRef.current[chunkIndex] = audio.duration;
          recalcTimelineDuration();
          applySeek();
        }
      };

      audio.addEventListener("loadedmetadata", () => {
        if (!ownsAudio()) return;
        updateChunkDuration();
        updatePlaybackFromAudio(audio);
      });

      audio.addEventListener("durationchange", () => {
        if (!ownsAudio()) return;
        updateChunkDuration();
        updatePlaybackFromAudio(audio);
      });

      audio.addEventListener("timeupdate", () => {
        if (!ownsAudio()) return;
        updatePlaybackFromAudio(audio);
      });

      audio.addEventListener("ended", () => {
        if (!ownsAudio()) return;
        storeDispatch({ type: "tts:highlight-clear" });
        storeDispatch({ type: "tts:playback-stop" });
        setChunkStatus(chunkIndex, {
          audio: "ready-cached",
        });
        const next = chunkIndex + 1;
        if (next < chunkPlanRef.current.length) {
          const nextToken = nextCommand({
            activeOutput: "main",
            status: autoplay ? "playing" : "paused",
            activity: null,
          });
          void playChunkRef.current?.(next, {
            sessionId: sessionId,
            commandId: nextToken.commandId,
            startAt: 0,
            force,
            autoplay,
          });
          return;
        }

        if (!commandIsCurrent()) return;
        audioRef.current = null;
        timestampsRef.current = [];
        currentChunkRef.current = null;
        currentChunkIndexRef.current = 0;
        lastHighlightLineRef.current = null;
        storeDispatch({ type: "tts:clear" });
        resetTransport();
      });

      audio.src = prepared.audioUrl;
      applySeek();
      if (!autoplay) {
        dispatchUi({ type: "status:set", status: "paused", activeOutput: "main" });
        setChunkStatus(chunkIndex, {
          audio: "ready-cached",
        });
        setActivity(null);
        updatePlaybackFromAudio(audio);
        return;
      }

      try {
        await audio.play();
      } catch (err: any) {
        if (!ownsAudio()) {
          abortAudioElement(audio);
          return;
        }
        const message = describePlaybackError(err, "Playback failed");
        reportTtsClientError(
          "audio_bar.play_chunk",
          "Audio playback failed for chunk",
          err,
          {
            chunkIndex,
            lineOffset: chunk.lineOffset,
            lineCount: chunk.lineCount,
          }
        );
        setChunkStatus(chunkIndex, {
          audio: "error",
          note: message,
        });
        throw err;
      }
      if (!ownsAudio()) {
        abortAudioElement(audio);
        return;
      }

      dispatchUi({ type: "status:set", status: "playing", activeOutput: "main" });
      setChunkStatus(chunkIndex, {
        audio: "playing",
      });
      setActivity(null);
      updatePlaybackFromAudio(audio);

      // Prefetch next 2 chunks for smoother transitions
      for (let ahead = 1; ahead <= 2; ahead++) {
        const upcoming = chunkPlanRef.current[chunkIndex + ahead];
        if (upcoming) {
          void ensurePreparedChunk(upcoming.index, {
            force: false,
            waitForTimestamps: false,
          }).catch(() => {
            // Best effort prefetch only.
          });
        }
      }

      // Extend warmup window when playback approaches the ceiling
      if (chunkIndex + 3 >= warmupCeilingRef.current && chunkIndex + 1 < chunkPlanRef.current.length) {
        void warmAllChunksRef.current?.(chunkPlanRef.current, false, chunkIndex + 1).catch(() => {});
      }
    },
    [
      applyChunkTimestamps,
      bumpDetails,
      clearMainAudio,
      dispatchUi,
      ensurePreparedChunk,
      isCurrentMainCommand,
      nextCommand,
      recalcTimelineDuration,
      reportTtsClientError,
      resetTransport,
      setActivity,
      setChunkStatus,
      storeDispatch,
      updatePlaybackFromAudio,
    ]
  );
  playChunkRef.current = playChunk;

  useEffect(() => {
    isMountedRef.current = true;
    const haltAudio = () => {
      nextCommand({
        activeOutput: "none",
        status: "stopping",
        newSession: true,
        activity: null,
        previewingVoice: null,
      });
      teardownAll();
      storeDispatch({ type: "tts:clear" });
      resetTransport();
    };

    window.addEventListener("pagehide", haltAudio);
    window.addEventListener("beforeunload", haltAudio);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("pagehide", haltAudio);
      window.removeEventListener("beforeunload", haltAudio);
      haltAudio();
    };
  }, [nextCommand, resetTransport, storeDispatch, teardownAll]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = state.volume;
    if (previewAudioRef.current) previewAudioRef.current.volume = state.volume;
  }, [state.volume]);

  useEffect(() => {
    if (state.ttsSettings.simpleMode || !state.ttsSettings.followAlong) {
      if (!state.ttsSettings.followAlong) {
        storeDispatch({ type: "tts:highlight-clear" });
      }
      storeDispatch({ type: "tts:playback-stop" });
    }
  }, [state.ttsSettings.followAlong, state.ttsSettings.simpleMode, storeDispatch]);

  useEffect(() => {
    stop();
  }, [openFilePath, stop]);

  const settingsKernelKey = useMemo(() => {
    return getTtsSettingsSignature(state.ttsSettings);
  }, [state.ttsSettings]);
  const lastKernelContentHashRef = useRef(hashStr(content || ""));
  const lastKernelSettingsKeyRef = useRef(settingsKernelKey);

  useEffect(() => {
    const nextChunks = content?.trim() ? buildTtsChunks(content, 1) : [];
    const contentHash = hashStr(content || "");
    const contentChanged = lastKernelContentHashRef.current !== contentHash;
    const settingsChanged = lastKernelSettingsKeyRef.current !== settingsKernelKey;
    const needsInitialPlan = nextChunks.length > 0 && chunkPlanRef.current.length === 0;
    if (!contentChanged && !settingsChanged && !needsInitialPlan) return;

    lastKernelContentHashRef.current = contentHash;
    lastKernelSettingsKeyRef.current = settingsKernelKey;

    ensureCore();

    // Any content or settings change invalidates the warmup window
    warmupCeilingRef.current = 0;
    fullPrepRunIdRef.current += 1;
    fullPrepPromiseRef.current = null;
    fullPrepKeyRef.current = "";

    if (settingsChanged) {
      prepGenerationRef.current += 1;
      chunkAudioPrepRef.current.clear();
      clearBlobUrls();
    }

    if (nextChunks.length === 0) {
      prepGenerationRef.current += 1;
      chunkAudioPrepRef.current.clear();
      clearBlobUrls();
      chunkPlanRef.current = [];
      preparedChunksRef.current.clear();
      chunkDurationsRef.current = [];
      chunkStatusRef.current.clear();
      bumpDetails();
      if (stateRef.current.status !== "idle") stop();
      return;
    }

    const prevChunks = chunkPlanRef.current;
    const prevPrepared = preparedChunksRef.current;
    const prevDurations = chunkDurationsRef.current;
    const prevStatus = chunkStatusRef.current;
    const prevIndexByKey = new Map<string, number>();
    for (const chunk of prevChunks) {
      prevIndexByKey.set(chunk.key, chunk.index);
    }

    const nextPrepared = new Map<number, TtsPreparedChunk>();
    const nextAudioPrep = new Map<number, Promise<TtsPreparedChunk>>();
    const nextDurations: number[] = [];
    const nextStatus = new Map<number, ChunkRuntimeStatus>();
    const prevAudioPrep = chunkAudioPrepRef.current;

    for (const chunk of nextChunks) {
      const estimate = estimateChunkDurationSeconds(chunk);
      const prevIndex = prevIndexByKey.get(chunk.key);
      const reusable = prevIndex !== undefined && !settingsChanged;
      if (!reusable) {
        nextDurations[chunk.index] = estimate;
        nextStatus.set(chunk.index, {
          audio: "pending",
          timing: "pending",
          note: settingsChanged ? "settings changed" : "queued",
        });
        continue;
      }

      const prepared = prevPrepared.get(prevIndex);
      if (prepared) {
        nextPrepared.set(chunk.index, { ...prepared, chunk });
      }
      const existingAudioPrep = prevAudioPrep.get(prevIndex);
      if (existingAudioPrep) {
        nextAudioPrep.set(chunk.index, existingAudioPrep);
      }
      nextDurations[chunk.index] = prevDurations[prevIndex] || estimate;
      const existingStatus = prevStatus.get(prevIndex);
      if (existingStatus) {
        nextStatus.set(chunk.index, {
          ...existingStatus,
          audio:
            stateRef.current.status === "idle" && existingStatus.audio === "playing"
              ? "ready-cached"
              : existingStatus.audio,
        });
      } else {
        nextStatus.set(chunk.index, {
          audio: prepared ? "ready-cached" : "pending",
          timing: prepared && prepared.timestamps.length > 0 ? "ready" : "pending",
          note: prepared ? "audio ready" : "queued",
        });
      }
    }

    chunkPlanRef.current = nextChunks;
    preparedChunksRef.current = nextPrepared;
    chunkAudioPrepRef.current = nextAudioPrep;
    chunkDurationsRef.current = nextDurations;
    chunkStatusRef.current = nextStatus;
    bumpDetails();

    if (stateRef.current.status !== "idle") {
      if (settingsChanged) {
        stop();
        return;
      }
      const activeChunk = currentChunkRef.current;
      if (activeChunk) {
        const stillExists = nextChunks.some((chunk) => chunk.key === activeChunk.key);
        if (!stillExists) stop();
      }
    }
  }, [bumpDetails, clearBlobUrls, content, ensureCore, settingsKernelKey, stop]);

  const playFrom = useCallback(
    async (fromTarget?: number | { fromLine: number; fromChar?: number }) => {
      if (!content?.trim()) return;
      await primeMediaPlayback().catch(() => {});

      stopPreview();
      const token = nextCommand({
        activeOutput: "main",
        status: "loading",
        newSession: true,
        activity: PENDING_ACTIVITY,
        resetTimeline: true,
      });

      ensureCore();

      const lines = content.split("\n");
      const requestedLine =
        typeof fromTarget === "number" ? fromTarget : fromTarget?.fromLine;
      const requestedChar =
        typeof fromTarget === "object" ? fromTarget.fromChar : undefined;
      const targetLine = Math.max(
        1,
        Math.min(lines.length, requestedLine && requestedLine > 1 ? requestedLine : 1)
      );
      const targetChar =
        Number.isFinite(requestedChar) && typeof requestedChar === "number"
          ? Math.max(0, Math.min(content.length - 1, requestedChar))
          : undefined;
      const hasTargetChar =
        typeof targetChar === "number" && Number.isFinite(targetChar);

      const chunks = buildTtsChunks(
        content,
        1,
        hasTargetChar ? targetChar : undefined
      );
      if (chunks.length === 0) return;
      const existingPlan = chunkPlanRef.current;
      const samePlan =
        existingPlan.length === chunks.length &&
        existingPlan.every(
          (chunk, idx) =>
            chunk.key === chunks[idx].key &&
            chunk.lineOffset === chunks[idx].lineOffset &&
            chunk.lineCount === chunks[idx].lineCount
        );

      const force = forceRegenRef.current;
      forceRegenRef.current = false;
      forceRunRef.current = force;

      clearMainAudio();

      chunkPlanRef.current = chunks;
      if (!samePlan || force) {
        prepGenerationRef.current += 1;
        fullPrepRunIdRef.current += 1;
        fullPrepPromiseRef.current = null;
        fullPrepKeyRef.current = "";
        preparedChunksRef.current.clear();
        chunkAudioPrepRef.current.clear();
        clearBlobUrls();
        chunkDurationsRef.current = chunks.map(estimateChunkDurationSeconds);
        chunkStatusRef.current = new Map(
          chunks.map((chunk) => [
            chunk.index,
            {
              audio: "pending",
              timing: "pending",
              note: force ? "forced regen" : "queued",
            } as ChunkRuntimeStatus,
          ])
        );
      } else {
        if (chunkDurationsRef.current.length !== chunks.length) {
          chunkDurationsRef.current = chunks.map(
            (chunk, idx) =>
              chunkDurationsRef.current[idx] || estimateChunkDurationSeconds(chunk)
          );
        }
        for (const chunk of chunks) {
          if (!chunkStatusRef.current.has(chunk.index)) {
            chunkStatusRef.current.set(chunk.index, {
              audio: "pending",
              timing: "pending",
              note: "queued",
            });
          }
        }
      }

      currentChunkRef.current = null;
      currentChunkIndexRef.current = 0;
      timestampsRef.current = [];
      missingTimingLoggedRef.current = false;
      lastHighlightLineRef.current = null;
      bumpDetails();

      const estimatedTotal = sum(chunkDurationsRef.current);
      dispatchUi({
        type: "transport:update",
        mediaDuration: estimatedTotal,
        timingDuration: estimatedTotal,
        progress: 0,
        currentTime: 0,
      });
      storeDispatch({ type: "tts:clear" });
      reportTtsClientLog("audio_bar.play_from.request", "Play-from requested", {
        level: "info",
        meta: {
          targetLine,
          targetChar,
          force,
          chunkCount: chunks.length,
        },
      });

      let targetChunkIndex = -1;
      if (hasTargetChar) {
        targetChunkIndex = 0;
      }
      if (targetChunkIndex === -1) {
        targetChunkIndex = chunks.findIndex(
          (chunk) =>
            targetLine >= chunk.lineOffset &&
            targetLine <= chunk.lineOffset + chunk.lineCount - 1
        );
      }
      if (targetChunkIndex === -1) targetChunkIndex = 0;
      const targetPrepared = preparedChunksRef.current.get(targetChunkIndex);
      const targetChunk = chunks[targetChunkIndex];
      const targetChunkChar =
        hasTargetChar && targetChunk ? targetChar - targetChunk.charOffset : undefined;
      const startAt = hasTargetChar
        ? 0
        : getPreciseSeekStart(
            targetPrepared?.timestamps || [],
            targetLine > 1 ? targetLine : undefined,
            targetChunkChar
          ) ?? 0;
      const preferPreciseSeek = !hasTargetChar && targetLine > 1;

      try {
        await playChunkRef.current?.(targetChunkIndex, {
          sessionId: token.sessionId,
          commandId: token.commandId,
          startAt,
          force,
          seekLine: targetLine > 1 ? targetLine : undefined,
          seekChar: targetChar,
          preferPreciseSeek,
          autoplay: true,
        });

        void warmAllChunks(chunks, force, targetChunkIndex).catch(() => {
          // Playback can continue even if background warmup fails.
        });
      } catch (err: any) {
        if (!isCurrentMainCommand(token.sessionId, token.commandId)) return;
        const message = describePlaybackError(err, "Playback failed");
        reportTtsClientError(
          "audio_bar.play_from",
          "Playback failed while starting from requested line",
          err,
          {
            fromLine: targetLine,
            fromChar: targetChar,
            targetChunkIndex,
            preferPreciseSeek,
          }
        );
        dispatchUi({ type: "error:set", error: message, status: "error" });
        resetTransport(message);
      }
    },
    [
      bumpDetails,
      clearBlobUrls,
      clearMainAudio,
      content,
      dispatchUi,
      ensureCore,
      isCurrentMainCommand,
      nextCommand,
      playChunk,
      reportTtsClientError,
      reportTtsClientLog,
      resetTransport,
      setActivity,
      stopPreview,
      storeDispatch,
      warmAllChunks,
    ]
  );

  useEffect(() => {
    if (ttsFromLine !== null) {
      void playFrom(ttsFromLine);
    }
  }, [ttsFromLine, playFrom]);

  const play = useCallback(() => {
    void primeMediaPlayback().catch(() => {});
    const pausedAudio = audioRef.current;
    if (
      stateRef.current.status === "paused" &&
      stateRef.current.activeOutput === "main" &&
      pausedAudio
    ) {
      const sessionId = sessionIdRef.current;
      const token = nextCommand({
        activeOutput: "main",
        status: "playing",
        activity: null,
        clearError: false,
      });
      pausedAudio
        .play()
        .then(() => {
          if (!isCurrentMainCommand(sessionId, token.commandId) || audioRef.current !== pausedAudio) {
            abortAudioElement(pausedAudio);
            return;
          }
          dispatchUi({ type: "status:set", status: "playing", activeOutput: "main" });
          updatePlaybackFromAudio(pausedAudio);
        })
        .catch((err) => {
          if (!isCurrentMainCommand(sessionId, token.commandId) || audioRef.current !== pausedAudio) {
            return;
          }
          const message = describePlaybackError(err, "Playback failed");
          reportTtsClientError(
            "audio_bar.resume_playback",
            "Resuming paused audio failed",
            err,
            {
              chunkIndex: currentChunkIndexRef.current,
            }
          );
          dispatchUi({ type: "error:set", error: message });
          dispatchUi({ type: "status:set", status: "paused", activeOutput: "main" });
        });
      return;
    }

    if (ownerRef.current === "preview") {
      stopPreview();
    }
    void playFrom();
  }, [
    dispatchUi,
    isCurrentMainCommand,
    nextCommand,
    playFrom,
    reportTtsClientError,
    stopPreview,
    updatePlaybackFromAudio,
  ]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    audio?.pause();
    if (audio) updatePlaybackFromAudio(audio);
    dispatchUi({ type: "status:set", status: "paused", activeOutput: "main" });
  }, [dispatchUi, updatePlaybackFromAudio]);

  const seekToGlobalTime = useCallback(
    (targetSeconds: number) => {
      const chunks = chunkPlanRef.current;
      if (chunks.length === 0) return;

      const total = sum(chunkDurationsRef.current);
      if (total <= 0) return;

      const safeTarget = Math.max(0, Math.min(total, targetSeconds));
      let acc = 0;
      let targetChunkIndex = chunks.length - 1;
      for (let i = 0; i < chunks.length; i++) {
        const dur = chunkDurationsRef.current[i] || estimateChunkDurationSeconds(chunks[i]);
        if (safeTarget <= acc + dur || i === chunks.length - 1) {
          targetChunkIndex = i;
          break;
        }
        acc += dur;
      }

      const withinChunk = Math.max(0, safeTarget - acc);
      if (targetChunkIndex === currentChunkIndexRef.current && audioRef.current) {
        audioRef.current.currentTime = withinChunk;
        updatePlaybackFromAudio(audioRef.current);
        return;
      }

      const command = nextCommand({
        activeOutput: "main",
        status: stateRef.current.status === "paused" ? "paused" : "loading",
        activity: null,
        clearError: false,
      });
      const playPromise = playChunkRef.current?.(targetChunkIndex, {
        sessionId: sessionIdRef.current,
        commandId: command.commandId,
        startAt: withinChunk,
        force: forceRunRef.current,
        autoplay: stateRef.current.status !== "paused",
      });
      if (playPromise) {
        void playPromise.catch((err) => {
          const message = describePlaybackError(err, "Playback failed");
          reportTtsClientError(
            "audio_bar.seek_to_time",
            "Failed to seek across chunks",
            err,
            {
              targetChunkIndex,
              targetSeconds: safeTarget,
              withinChunk,
            }
          );
          dispatchUi({ type: "error:set", error: message });
          resetTransport(message);
        });
      }
    },
    [dispatchUi, nextCommand, reportTtsClientError, resetTransport, updatePlaybackFromAudio]
  );

  const skipBack15 = useCallback(() => {
    seekToGlobalTime(Math.max(0, stateRef.current.currentTime - 15));
  }, [seekToGlobalTime]);

  const skipNextChunk = useCallback(() => {
    const chunks = chunkPlanRef.current;
    if (chunks.length === 0) return;
    const currentLine = lastHighlightLineRef.current ?? 1;
    const logicalChunks = getChunkStartLines(content || "");
    const nextChunkLine = logicalChunks.find((line) => line > currentLine);
    if (!nextChunkLine) return;

    let targetChunkIndex = chunks.findIndex(
      (chunk) => nextChunkLine <= chunk.lineOffset + chunk.lineCount - 1
    );
    if (targetChunkIndex === -1) targetChunkIndex = chunks.length - 1;

    if (targetChunkIndex === currentChunkIndexRef.current && audioRef.current) {
      const entry = timestampsRef.current.find((ts) => ts.line >= nextChunkLine);
      if (entry) {
        audioRef.current.currentTime = entry.start;
        updatePlaybackFromAudio(audioRef.current);
      }
      return;
    }

    const prepared = preparedChunksRef.current.get(targetChunkIndex);
    const startAt = prepared?.timestamps.find((ts) => ts.line >= nextChunkLine)?.start ?? 0;
    const command = nextCommand({
      activeOutput: "main",
      status: stateRef.current.status === "paused" ? "paused" : "loading",
      activity: null,
      clearError: false,
    });
    const playPromise = playChunkRef.current?.(targetChunkIndex, {
      sessionId: sessionIdRef.current,
      commandId: command.commandId,
      startAt,
      force: forceRunRef.current,
      autoplay: stateRef.current.status !== "paused",
    });
    if (playPromise) {
      void playPromise.catch((err) => {
        const message = describePlaybackError(err, "Playback failed");
        reportTtsClientError(
          "audio_bar.skip_next_chunk",
          "Skipping to next chunk failed",
          err,
          {
            targetChunkIndex,
            nextChunkLine,
          }
        );
        dispatchUi({ type: "error:set", error: message });
        resetTransport(message);
      });
    }
  }, [content, dispatchUi, nextCommand, reportTtsClientError, resetTransport, updatePlaybackFromAudio]);

  const previewVoice = useCallback(
    async ({
      providerId,
      voiceId,
      modelId,
      event,
    }: {
      providerId: TtsSettings["provider"];
      voiceId: string;
      modelId: string;
      event: ReactMouseEvent;
    }) => {
      event.stopPropagation();
      const previewKey = makePreviewKey(providerId, voiceId);

      if (stateRef.current.previewingVoice === previewKey && previewAudioRef.current) {
        stopPreview();
        return;
      }

      stop();

      const token = nextCommand({
        activeOutput: "preview",
        status: "previewing",
        previewingVoice: previewKey,
        activity: null,
      });

      try {
        await primeMediaPlayback().catch(() => {});
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!isCurrentCommand(token.commandId)) return;
        const tokenString = session?.access_token ?? "";
        const audio = configurePlaybackAudio(new Audio(), volumeRef.current);
        audio.src = `/api/tts/preview?provider=${encodeURIComponent(providerId)}&voiceId=${encodeURIComponent(voiceId)}&modelId=${encodeURIComponent(modelId)}&token=${encodeURIComponent(tokenString)}`;
        audio.addEventListener("ended", () => {
          if (!isCurrentCommand(token.commandId) || previewAudioRef.current !== audio) return;
          ownerRef.current = "none";
          clearPreviewAudio();
          dispatchUi({ type: "preview:set", previewingVoice: null });
        });
        if (!isCurrentCommand(token.commandId)) {
          abortAudioElement(audio);
          return;
        }
        previewAudioRef.current = audio;
        await audio.play();
      } catch (err) {
        if (!isCurrentCommand(token.commandId)) return;
        const message = describePlaybackError(err, "Preview failed");
        reportTtsClientError(
          "audio_bar.preview_voice",
          "Voice preview playback failed",
          err,
          { voiceId, providerId }
        );
        ownerRef.current = "none";
        clearPreviewAudio();
        dispatchUi({ type: "preview:set", previewingVoice: null });
        dispatchUi({
          type: "error:set",
          error: message,
        });
      }
    },
    [
      clearPreviewAudio,
      dispatchUi,
      isCurrentCommand,
      nextCommand,
      reportTtsClientError,
      stop,
      stopPreview,
    ]
  );

  const downloadAudio = useCallback(async () => {
    if (!content?.trim() || downloadBusy) return;
    const voiceName = getCurrentTtsVoice(ttsSettingsRef.current).name ?? "voice";
    const baseName = (openFilePath?.split("/").pop() ?? "audio").replace(/\.[^.]+$/, "");
    const chunks = buildTtsChunks(content, 1);
    if (chunks.length === 0) return;

    const samePlan =
      chunkPlanRef.current.length === chunks.length &&
      chunkPlanRef.current.every(
        (chunk, idx) =>
          chunk.key === chunks[idx].key &&
          chunk.lineOffset === chunks[idx].lineOffset &&
          chunk.lineCount === chunks[idx].lineCount
      );
    if (!samePlan) {
      fullPrepRunIdRef.current += 1;
      fullPrepPromiseRef.current = null;
      fullPrepKeyRef.current = "";
      chunkPlanRef.current = chunks;
      preparedChunksRef.current.clear();
      chunkAudioPrepRef.current.clear();
      clearBlobUrls();
      chunkDurationsRef.current = chunks.map(estimateChunkDurationSeconds);
      chunkStatusRef.current = new Map(
        chunks.map((chunk) => [
          chunk.index,
          { audio: "pending", timing: "pending", note: "queued" } as ChunkRuntimeStatus,
        ])
      );
      bumpDetails();
    }

    setDownloadBusy(true);
    clearError();
    setActivity(`Preparing full audio export (0/${chunks.length})...`);

    try {
      const buffers: ArrayBuffer[] = [];
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        setChunkStatus(chunk.index, {
          audio: "preparing",
          note: `export ${i + 1}/${chunks.length}`,
        });

        const prepared = await ensurePreparedChunk(chunk.index, {
          force: false,
          waitForTimestamps: false,
        });

        setChunkStatus(chunk.index, {
          audio: "ready-cached",
          timing: prepared.timestamps.length > 0 ? "ready" : "pending",
          note: `export ${i + 1}/${chunks.length}`,
        });

        const res = await fetch(prepared.audioUrl);
        if (!res.ok) {
          throw new Error(`Failed to export chunk ${chunk.index + 1} (${res.status})`);
        }
        const bytes = await res.arrayBuffer();
        buffers.push(bytes);
        setActivity(`Preparing full audio export (${i + 1}/${chunks.length})...`);
      }

      const blob = new Blob(buffers, { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}-${voiceName}.mp3`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setActivity(null);
    } catch (err) {
      reportTtsClientError(
        "audio_bar.download_audio",
        "Full-document audio download failed",
        err,
        {
          fileName: openFilePath || "",
          chunkCount: chunks.length,
        }
      );
      dispatchUi({ type: "error:set", error: "Download failed" });
      setActivity(null);
    } finally {
      setDownloadBusy(false);
    }
  }, [
    bumpDetails,
    clearBlobUrls,
    clearError,
    content,
    downloadBusy,
    ensurePreparedChunk,
    openFilePath,
    reportTtsClientError,
    setActivity,
    setChunkStatus,
  ]);

  const regenerate = useCallback(() => {
    forceRegenRef.current = true;
    stop();
    void playFrom();
  }, [playFrom, stop]);

  const currentVoice = getCurrentTtsVoice(state.ttsSettings);
  const chunkPlan = chunkPlanRef.current;
  const preparedChunkCount = Array.from(chunkStatusRef.current.values()).filter((status) =>
    status.audio === "ready-cached" || status.audio === "playing"
  ).length;
  const timestampReadyCount = Array.from(chunkStatusRef.current.values()).filter(
    (status) => status.timing === "ready"
  ).length;
  const effectiveDuration =
    state.mediaDuration > 0 ? state.mediaDuration : state.timingDuration;

  return {
    state,
    ttsSettings: state.ttsSettings,
    setTtsSettings,
    clearError,
    setVolume,
    play,
    playFrom,
    pause,
    stop,
    stopPreview,
    seekToGlobalTime,
    skipBack15,
    skipNextChunk,
    previewVoice,
    downloadAudio,
    regenerate,
    effectiveDuration,
    hasContent: Boolean(content?.trim()),
    isMainActive: state.activeOutput === "main" && state.status !== "idle",
    currentVoiceName: currentVoice?.name ?? "Voice",
    providerName: getTtsProvider(state.ttsSettings.provider).name,
    chunkPlan,
    chunkStatuses: chunkStatusRef.current,
    chunkDurations: chunkDurationsRef.current,
    currentChunkIndex: currentChunkRef.current?.index ?? null,
    currentChunkNumber: currentChunkRef.current ? currentChunkRef.current.index + 1 : 0,
    preparedChunkCount,
    timestampReadyCount,
    downloadBusy,
    followAlong: state.ttsSettings.followAlong,
  };
}
